import React, {
  UIEventHandler,
  useCallback,
  useRef,
  useEffect,
  useState
} from 'react'

import { Column, IconButton, Spinner, Row } from '../../../../components'
import { useWindowThrottleScrollEnd } from '../../../../hooks'

import { LOGS_LIMIT } from '../../logs'

import { ListItem } from '../logs-list-item'

import { ItemsListProps } from './items-list.types'
import { useStyle } from './items-list.styles'

export const ItemsList = ({
  isLoaded,
  data,
  catalog,
  current,
  next,
  minHeight = 800,
  onClick,
  onLoadMore
}: ItemsListProps) => {
  const [scrollTop, changeScrollTop] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const [height, changeHeight] = useState(minHeight)
  const classes = useStyle({ height })
  const listRef = useRef<HTMLDivElement>(null)

  const handleOnScrollToTop = useCallback(() => {
    if (listRef.current) {
      listRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
    }
  }, [listRef])

  const handleOnLoadMore = useCallback(() => {
    const isAllowedToLoadMore = current && next
    const isLoadMore = isAllowedToLoadMore && isLoaded
    const isLoadAllowed = isLoadMore && onLoadMore

    if (isLoadAllowed) {
      onLoadMore({
        offset: current,
        limit: LOGS_LIMIT
      })
    }
  }, [current, isLoaded, onLoadMore])

  const handleOnScroll: UIEventHandler<HTMLDivElement> = (event) => {
    const currentTarget = event.target as HTMLDivElement | null

    if (currentTarget) {
      const { scrollTop: currentScrollTop, scrollHeight } = currentTarget

      const isScrollToTop = currentScrollTop > scrollHeight * 0.1
      changeScrollTop(isScrollToTop)
    }
  }

  useWindowThrottleScrollEnd(handleOnLoadMore)
  useEffect(() => {
    if (containerRef.current) {
      const { innerHeight } = window
      const OTHER_CONTENT_HEIGHT = 226
      const containerHeight = innerHeight - OTHER_CONTENT_HEIGHT

      changeHeight(containerHeight)
    }
  }, [containerRef.current])

  return (
    <Column fullHeight fullWidth className={classes.list}>
      <Column
        ref={containerRef}
        fullWidth
        className={classes.outerContainer}
        onScroll={handleOnScroll}
      >
        <Column fullWidth className={classes.logsList} ref={listRef}>
          {data.map((log) => (
            <ListItem
              key={`log_${log.createdAt}_${log.id}`}
              {...log}
              catalog={catalog}
              onClick={onClick}
            />
          ))}
        </Column>
      </Column>
      {!isLoaded && <Spinner className={classes.loader} />}
      {scrollTop && (
        <Row fullWidth justifyContent="flex-end">
          <IconButton
            className={classes.goUp}
            name="ARROW_BACK"
            fill="darkBlue"
            preset="up"
            onClick={handleOnScrollToTop}
          />
        </Row>
      )}
    </Column>
  )
}
